import React, { useMemo, useState, useEffect, useRef } from 'react';
import { AppointmentMeHey, getDayAppointments, getDayOfWeekThaiLong } from './AppointmentMeHeyUtils';
import { formatThaiMonth } from 'react-lib/utils/dateUtils';
import { convertStringToColor } from "react-lib/apps/MEH/sequence/ScheduleSetting";

interface AppointmentDayViewMeHeyProps {
  currentDate: Date;
  appointments: AppointmentMeHey[];
  onAppointmentClick?: (appointment: AppointmentMeHey) => void;
  selectedAppointmentId?: number | null;
}

const AppointmentDayViewMeHey: React.FC<AppointmentDayViewMeHeyProps> = (props) => {
  const dayAppointments = getDayAppointments(props.appointments || [], props.currentDate);
  const hours = Array.from({ length: 24 }, (_, i) => i);

  // Group appointments by doctor
  const doctorColumns = useMemo(() => {
    const doctorMap = new Map<string, AppointmentMeHey[]>();

    dayAppointments.forEach(apt => {
      const doctor = apt.doctor || apt.title; // Use doctor field or title as fallback
      if (!doctorMap.has(doctor)) {
        doctorMap.set(doctor, []);
      }
      doctorMap.get(doctor)!.push(apt);
    });

    return Array.from(doctorMap.entries());
  }, [dayAppointments]);

  const getAppointmentStyle = (apt: AppointmentMeHey): { top: number; height: number } | null => {
    if (!apt.startTime || !apt.endTime) return null;

    const startHour = apt.startTime.getHours();
    const startMinute = apt.startTime.getMinutes();
    const endHour = apt.endTime.getHours();
    const endMinute = apt.endTime.getMinutes();

    // Calculate position (60px per hour)
    const top = (startHour * 60) + (startMinute);
    const endPosition = (endHour * 60) + (endMinute);
    const height = endPosition - top;

    return { top, height };
  };

  const formatTimeRange = (apt: AppointmentMeHey): string => {
    const startHour = apt.startTime.getHours().toString().padStart(2, '0');
    const startMin = apt.startTime.getMinutes().toString().padStart(2, '0');
    const endHour = apt.endTime.getHours().toString().padStart(2, '0');
    const endMin = apt.endTime.getMinutes().toString().padStart(2, '0');

    return `${startHour}:${startMin} - ${endHour}:${endMin}`;
  };

  // Calculate current time indicator position
  const getCurrentTimePosition = (): number | null => {
    const now = new Date();
    const todayDate = props.currentDate.toDateString();
    const nowDate = now.toDateString();

    // Only show indicator if viewing today
    if (todayDate !== nowDate) return null;

    const hours = now.getHours();
    const minutes = now.getMinutes();

    // Position in pixels (60px per hour)
    return (hours * 60) + minutes;
  };

  // State for real-time current time position
  const [currentTimePosition, setCurrentTimePosition] = useState<number | null>(getCurrentTimePosition());

  // Ref to store interval ID for cleanup
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Update current time position every minute for real-time updates, synced to 0 seconds
  useEffect(() => {
    const updateTimePosition = () => {
      setCurrentTimePosition(getCurrentTimePosition());
    };

    // Update immediately
    updateTimePosition();

    // Calculate milliseconds until the next minute (when seconds = 0)
    const now = new Date();
    const msUntilNextMinute = (60 - now.getSeconds()) * 1000 - now.getMilliseconds();

    // Wait until the next minute, then start interval
    const timeoutId = setTimeout(() => {
      updateTimePosition(); // Update at 0 seconds

      // Then update every minute (60000ms) starting from 0 seconds
      intervalRef.current = setInterval(updateTimePosition, 60000);
    }, msUntilNextMinute);

    // Cleanup timeout and interval on unmount
    return () => {
      clearTimeout(timeoutId);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [props.currentDate]);

  return (
    <div className="day-view">
      {/* Day header */}
      <div className="day-view-header">
        <div className="day-view-title-single">
          {getDayOfWeekThaiLong(props.currentDate.getDay())}, {props.currentDate.getDate()} {formatThaiMonth(props.currentDate)} {props.currentDate.getFullYear() + 543}
        </div>
      </div>

      {/* Time grid with doctor columns - SCROLLABLE */}
      <div className="day-view-body-scrollable">
        <div className="day-view-body">
          {/* Row 1, Col 1: Time Header Spacer */}
          <div className="day-time-header-spacer"></div>

          {/* Row 1, Col 2: Doctor Headers */}
          <div className="day-doctors-header">
            {doctorColumns.map(([doctor], index) => (
              <div key={index} className="day-doctor-header">
                <div className="day-doctor-name">{doctor}</div>
              </div>
            ))}
          </div>

          {/* Row 2, Col 1: Time Column */}
          <div className="day-time-gutter-column">
            {hours.map((hour) => (
              <div key={hour} className="day-time-slot">
                <span className="day-time-label">
                  {hour.toString().padStart(2, '0')}:00
                </span>
              </div>
            ))}
          </div>

          {/* Row 2, Col 2: Grid and Appointments */}
          <div className="day-doctors-grid">
            {/* Grid Lines */}
            <div className="day-grid-lines-multi">
              {hours.map((hour) => (
                <div key={hour} className="day-grid-line"></div>
              ))}
            </div>

            {/* Current Time Indicator */}
            {currentTimePosition !== null && (
              <div
                className="current-time-indicator"
                style={{ top: `${currentTimePosition}px` }}
              >
                <span className="current-time-label">
                  {new Date().getHours().toString().padStart(2, '0')}:
                  {new Date().getMinutes().toString().padStart(2, '0')}
                </span>
              </div>
            )}

            {/* Doctor Columns */}
            {doctorColumns.map(([doctor, doctorApts], columnIndex) => (
              <div key={columnIndex} className={`day-doctor-column ${columnIndex === 0 ? 'first-column' : ''}`}>
                {doctorApts.map((apt) => {
                  console.log('apt: ', apt);
                  const style = getAppointmentStyle(apt);
                  if (!style) return null;

                  const doctorName = apt.doctor || apt.title || 'default';
                  const colors = convertStringToColor(doctorName);
                  const isSelected = props.selectedAppointmentId === apt.doctor_dsb_id;

                  return (
                    <div
                      key={apt.doctor_dsb_id}
                      className={`day-rounds-multi ${isSelected ? "selected" : ""}`}
                      style={{
                        top: `${style.top}px`,
                        height: `${style.height}px`,
                        backgroundColor: colors.dark,
                        color: colors.textDark,
                        border: `2px solid ${isSelected ? colors.textDark : colors.medium}`,
                      }}
                      onClick={() => props.onAppointmentClick && props.onAppointmentClick(apt)}
                    >
                      <div className="rounds-time-range">{formatTimeRange(apt)}</div>
                      <div className="rounds-title">{apt.title}</div>
                      {apt.location && <div className="appointment-location">{apt.location}</div>}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppointmentDayViewMeHey;